import { useState } from "react";
import { Group } from "@mantine/core";
import { InlineTextInput } from "components/UI/InlineTextInput";
import { useUserTracks } from "stores/useUserTracks";
import { useTranslation } from "react-i18next";
import { SavingStatus } from "types/app";
import { showErrorNotification } from "utils/notifications/customNotifications";

import { CircleColorPicker } from "./ColorPicker";

type Props = {
  trackName: string | undefined;
  status: SavingStatus;
};

export const TrackMeta = ({ trackName, status }: Props) => {
  const { updateTrack, selectedTrack } = useUserTracks();
  const [nameBeingSaved, setNameBeingSaved] = useState<string | undefined>();
  const { t } = useTranslation();

  const handleNameChange = async (name: string) => {
    setNameBeingSaved(name);
    if (!selectedTrack) return;
    try {
      await updateTrack(selectedTrack?.id, { name });
    } catch {
      showErrorNotification({
        title: t("generic.error_title"),
        message: t("error_modal.generic_text"),
      });
    } finally {
      // On failure the saved name is shown again
      setNameBeingSaved(undefined);
    }
  };

  return (
    <Group
      gap={6}
      style={{
        flexGrow: 1,
        height: 28,
        flexWrap: "nowrap",
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      <CircleColorPicker />
      <InlineTextInput
        disabled={!selectedTrack || status === "saving"}
        value={
          nameBeingSaved ||
          trackName ||
          t("editmode.unsaved_track_name_placeholder")
        }
        onChange={(name) => void handleNameChange(name)}
      />
    </Group>
  );
};
